import { type CalendarDate, getLocalTimeZone, toCalendarDate, today } from '@internationalized/date';
import { useContext, useState } from 'react';
import {
  Calendar as RACCalendar,
  type CalendarProps as RACCalendarProps,
  type DateValue as RACDateValue,
  type DatePickerProps as RACDatePickerProps,
  DatePickerStateContext as RACDatePickerStateContext,
} from 'react-aria-components';
import { MonthView } from './views/month/month-view.js';
import { MultiYearView } from './views/multi-year/multi-year-view.js';
import { type DatePickerView, DatePickerViewProvider } from './views/view-provider.js';
import { YearView } from './views/year/year-view.js';

interface DatePickerCalendarProps<T extends RACDateValue>
  extends
    Pick<RACDatePickerProps<T>, 'placeholderValue'>,
    Pick<RACCalendarProps<T>, 'focusedValue' | 'defaultFocusedValue'> {}

/** @internal */
export function DatePickerCalendar<T extends RACDateValue>({
  focusedValue,
  defaultFocusedValue,
  placeholderValue,
}: DatePickerCalendarProps<T>) {
  const datePickerState = useContext(RACDatePickerStateContext);
  const [view, setView] = useState<DatePickerView>('month');
  const [focusedDate, setFocusedDate] = useState<CalendarDate>(() =>
    toCalendarDate(
      focusedValue ?? datePickerState?.value ?? defaultFocusedValue ?? placeholderValue ?? today(getLocalTimeZone()),
    ),
  );

  // `RACCalendar` has to be rendered unconditionally so that we have access to the calendar state.
  // However, `RACCalendar` assumes that it's always displaying the month view, and keeps announcing the focused day to screen readers on every change.
  // To prevent this, we control the focused date in `RACCalendar` with a separate state that isn't updated when the month view is hidden.
  const [monthViewFocusedDate, setMonthViewFocusedDate] = useState<CalendarDate>(focusedDate);

  return (
    <DatePickerViewProvider
      currentView={view}
      switchToView={setView}
      focusedDate={focusedDate}
      setFocusedDate={setFocusedDate}
    >
      <RACCalendar
        focusedValue={view === 'month' ? focusedDate : monthViewFocusedDate}
        onFocusChange={(date) => {
          setMonthViewFocusedDate(date);
          setFocusedDate(date);
        }}
      >
        {view === 'month' ? (
          <MonthView />
        ) : view === 'year' ? (
          <YearView />
        ) : view === 'multi-year' ? (
          <MultiYearView />
        ) : (
          `Unsupported view '${view}'`
        )}
      </RACCalendar>
    </DatePickerViewProvider>
  );
}
